"use client";

import Breadcrumb from "@/components/Breadcrumb";
import { ArrowRight, Calendar } from "lucide-react";
import { Link } from "@/i18n/routing";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { SITE_URL, normalizeLocale, sitePageUrl, ogLocaleTag } from "@/lib/site";

async function getBlogs() {
  try {
    const res = await fetch("/api/blogs");
    const data = await res.json();
    return Array.isArray(data?.blogs) ? data.blogs : [];
  } catch (error) {
    console.error("Error fetching blogs:", error);
    return [];
  }
}

// Excerpts are plain text in cards, so turn [label](url) into just the label.
function stripMarkdownLinks(text) {
  return String(text || "").replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function BlogPage({ params }) {
  const locale = normalizeLocale(params?.locale);
  const canonical = sitePageUrl(locale, "blog");
  const homeUrl = sitePageUrl(locale);

  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    getBlogs().then((data) => {
      if (active) {
        setBlogs(data);
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const breadcrumbItems = [
    { label: "Home", href: "/" },
    { label: "Blog" },
  ];

  const blogIndexLd = JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": `${canonical}#webpage`,
        url: canonical,
        name: "Insights & Ideas | Highlandgroup Blog",
        description:
          "Thoughts on strategy, design, technology and building better digital products from Highland Group.",
        inLanguage: locale,
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${canonical}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: homeUrl },
          { "@type": "ListItem", position: 2, name: "Blog", item: canonical },
        ],
      },
    ],
  });

  return (
    <main className="min-h-screen bg-bg-soft text-charcoal selection:bg-accent-red/20 selection:text-charcoal">
      <Helmet>
        <title>Insights & Ideas | Highlandgroup Blog</title>
        <meta
          name="description"
          content="Thoughts on strategy, design, technology and building better digital products. Practical perspectives from the team at Highland Group."
        />
        <meta
          name="keywords"
          content="Highland Group blog, digital strategy, product development, Switzerland"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Insights & Ideas | Highlandgroup" />
        <meta
          property="og:description"
          content="Practical perspectives on strategy, design, technology, and building better digital products."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content={ogLocaleTag(locale)} />
        <meta property="og:site_name" content="Highlandgroup" />
        <meta property="og:image" content={`${SITE_URL}/logo.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Insights & Ideas | Highlandgroup" />
        <meta
          name="twitter:description"
          content="Practical perspectives from the team at Highland Group."
        />
        <meta name="twitter:image" content={`${SITE_URL}/logo.png`} />
        <script type="application/ld+json">{blogIndexLd}</script>
      </Helmet>
      {/* Breadcrumb */}
      <Breadcrumb
        items={breadcrumbItems}
        title="Insights & Ideas"
        subtitle="Thoughts on strategy, design, technology and building better digital products"
        description="Practical perspectives from the team at Highland Group."
      />

      {loading ? (
        <section className="py-20 text-center text-muted">Loading articles…</section>
      ) : blogs.length === 0 ? (
        <section className="py-20 text-center text-muted">
          No articles published yet. Check back soon.
        </section>
      ) : (
        <section className="py-16">
          <div className="container mx-auto px-5 sm:px-8 md:px-12 lg:px-16">
            <div className="grid gap-6 sm:gap-7 md:gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {blogs.map((post) => (
                <article
                  key={post.slug}
                  className="
                    group relative flex flex-col rounded-2xl border border-border-light/70 bg-bg-main
                    overflow-hidden shadow-sm transition-all duration-300
                    hover:-translate-y-1.5 hover:shadow-xl hover:shadow-accent-red/10
                    hover:border-accent-red/40
                  "
                >
                  {/* Image */}
                  <Link href={`/blog/${post.slug}`} className="aspect-[16/9] relative overflow-hidden block bg-bg-soft">
                    <img
                      src={post.image || "/logo.png"}
                      alt={post.title}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                  </Link>

                  {/* Content */}
                  <div className="flex flex-col flex-1 p-5 md:p-6">
                    <Link href={`/blog/${post.slug}`} className="group/link flex flex-col flex-1">
                      {post.publishedDate && (
                        <span className="inline-flex items-center gap-1.5 text-xs text-muted mb-3">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatDate(post.publishedDate)}
                        </span>
                      )}

                      <h3 className="text-lg font-bold text-charcoal leading-snug mb-2 line-clamp-2 group-hover:text-accent-red transition-colors">
                        {post.title}
                      </h3>

                      <p className="text-muted leading-relaxed text-sm line-clamp-3 mb-4">
                        {stripMarkdownLinks(post.excerpt)}
                      </p>

                      <div className="mt-auto flex items-center gap-2 text-accent-red font-semibold text-sm">
                        Read more
                        <ArrowRight className="h-4 w-4 transition-transform group-hover/link:translate-x-1" />
                      </div>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
